import { driver, type DriveStep, type Driver, type PopoverDOM } from "driver.js";

export const LICENSE_SETUP_TOUR_ID = "license-setup";
export const LICENSE_SETUP_TOUR_PATH = "/admin/licenses/new";
export const LICENSE_SETUP_TOUR_HREF = `${LICENSE_SETUP_TOUR_PATH}?tour=${LICENSE_SETUP_TOUR_ID}`;

type Translate = (key: string, fallback: string) => string;

type LicenseSetupTourOptions = {
  t: Translate;
  canIssue: boolean;
  reduceMotion?: boolean;
  onNextClick?: (element: Element | undefined, step: DriveStep, options: { driver: Driver }) => void;
  onPrevClick?: (element: Element | undefined, step: DriveStep, options: { driver: Driver }) => void;
  onDestroyed?: () => void;
};

export type GuidedTourOptions = {
  t: Translate;
  name: string;
  steps: DriveStep[];
  reduceMotion?: boolean;
  onNextClick?: (element: Element | undefined, step: DriveStep, options: { driver: Driver }) => void;
  onPrevClick?: (element: Element | undefined, step: DriveStep, options: { driver: Driver }) => void;
  onDestroyed?: () => void;
};

export function buildLicenseSetupTourSteps(t: Translate, canIssue: boolean): DriveStep[] {
  return [
    {
      element: '[data-tour-id="license-context"]',
      popover: {
        title: t("help.tour.licenseSetup.contextTitle", "Choose Customer and Product"),
        description: t("help.tour.licenseSetup.contextDescription", "Start with the Customer receiving the grant and the Product whose feature catalog defines the available Entitlements."),
        side: "bottom",
        align: "start",
      },
    },
    {
      element: '[data-tour-id="license-validity"]',
      popover: {
        title: t("help.tour.licenseSetup.validityTitle", "Set the validity model"),
        description: t("help.tour.licenseSetup.validityDescription", "Choose the License model, optionally schedule when Activation becomes available, and set an absolute expiry when the model allows or requires one."),
        side: "bottom",
        align: "end",
      },
    },
    {
      element: '[data-tour-id="license-entitlements"]',
      popover: {
        title: t("help.tour.licenseSetup.entitlementsTitle", "Grant Entitlements"),
        description: t("help.tour.licenseSetup.entitlementsDescription", "Configure the runtime capabilities and limits exposed by the selected Product's feature catalog."),
        side: "right",
        align: "start",
      },
    },
    {
      element: '[data-tour-id="license-device-policy"]',
      popover: {
        title: t("help.tour.licenseSetup.policyTitle", "Define the Device Policy"),
        description: t("help.tour.licenseSetup.policyDescription", "Set the device limit, offline grace period, and required online check interval for this License."),
        side: "left",
        align: "start",
      },
    },
    {
      element: '[data-tour-id="license-issue"]',
      popover: {
        title: t("help.tour.licenseSetup.issueTitle", "Review and issue"),
        description: canIssue
          ? t("help.tour.licenseSetup.issueDescription", "Record the approval reason, review the configuration, then Issue license. This action creates the License and is recorded in the Audit Log.")
          : t("help.tour.licenseSetup.issueViewerDescription", "Your role can review this setup, but issuing a License requires License Manager or Security Admin permission."),
        side: "top",
        align: "end",
      },
    },
  ];
}

export function createLicenseSetupTour({
  t,
  canIssue,
  reduceMotion = prefersReducedMotion(),
  onNextClick,
  onPrevClick,
  onDestroyed,
}: LicenseSetupTourOptions): Driver {
  return createGuidedTour({
    t,
    name: t("help.tour.licenseSetupName", "License setup"),
    steps: buildLicenseSetupTourSteps(t, canIssue),
    reduceMotion,
    onNextClick,
    onPrevClick,
    onDestroyed,
  });
}

export function createGuidedTour({
  t,
  name,
  steps,
  reduceMotion = prefersReducedMotion(),
  onNextClick,
  onPrevClick,
  onDestroyed,
}: GuidedTourOptions): Driver {
  let didNotifyDestroyed = false;
  const notifyDestroyed = () => {
    if (didNotifyDestroyed) return;
    didNotifyDestroyed = true;
    onDestroyed?.();
  };
  let tour: Driver;
  const requestDestroy = () => {
    notifyDestroyed();
    tour.destroy();
  };

  tour = driver({
    steps,
    animate: !reduceMotion,
    duration: 200,
    overlayColor: "#000",
    overlayOpacity: 0.72,
    smoothScroll: !reduceMotion,
    allowClose: true,
    allowScroll: true,
    overlayClickBehavior: "close",
    stagePadding: 8,
    stageRadius: 8,
    disableActiveInteraction: true,
    skipMissingElement: true,
    waitForElement: 1_500,
    allowKeyboardControl: true,
    popoverClass: "tb-feature-tour",
    popoverOffset: 10,
    showButtons: ["next", "previous"],
    showProgress: true,
    progressText: `{{current}} / {{total}} · ${name}`,
    nextBtnText: t("help.tour.next", "Next"),
    prevBtnText: t("common.back", "Back"),
    doneBtnText: t("help.tour.finish", "Finish"),
    onNextClick: onNextClick
      ? (element, step, options) => onNextClick(element, step, options as { driver: Driver })
      : undefined,
    onPrevClick: onPrevClick
      ? (element, step, options) => onPrevClick(element, step, options as { driver: Driver })
      : undefined,
    onPopoverRender: (popover) => renderCoachmark(popover, requestDestroy, t),
    onDestroyStarted: (_element, _step, options) => {
      notifyDestroyed();
      options.driver.destroy();
    },
    onDestroyed: notifyDestroyed,
  });
  return tour;
}

export function restoreFocusAfterTour(target: HTMLElement | null) {
  if (!target) return;
  const restore = () => {
    if (target.isConnected) target.focus();
  };
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(restore);
    return;
  }
  restore();
}

function renderCoachmark(popover: PopoverDOM, requestDestroy: () => void, t: Translate) {
  popover.wrapper.classList.add("tb-feature-tour");
  popover.wrapper.insertBefore(popover.progress, popover.title);
  popover.closeButton.hidden = true;

  if (popover.footer.querySelector(".tb-feature-tour-skip")) return;
  const skip = document.createElement("button");
  skip.type = "button";
  skip.className = "tb-feature-tour-skip";
  skip.textContent = t("help.tour.skipTour", "Skip tour");
  skip.addEventListener("click", requestDestroy);
  popover.footer.insertBefore(skip, popover.footerButtons);
}

function prefersReducedMotion() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
